import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiMapPin, FiCalendar } from 'react-icons/fi';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { getRepresentations, getRepresentationById, getPostersByPrayerCenter } from '../api/publicApi';
import useFetch from '../hooks/useFetch';
import { SkeletonCountryBtn, SkeletonRepDetail } from '../components/Skeleton';
import { toEmbedUrl } from '../utils/mapUtils';

/* ── Label helper ── */
function pcLabel(type_lieu, nom) {
  if (type_lieu === 'espace_faustine') return 'ESPACE STE FAUSTINE';
  if (type_lieu === 'atmosphere_victoire') return 'ATMOSPHÈRE DE LA VICTOIRE';
  return nom?.toUpperCase() || '';
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso)
    .toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    .toUpperCase();
}

/* ── Per-prayer-center block that fetches & shows its posters ── */
function PrayerCenterBlock({ pc }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [posters, setPosters] = useState(null); // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const handleTitleClick = async () => {
    const targetEvent = posters?.[activeIdx] || posters?.[0] || null;
    if (targetEvent) {
      const previewUrl = targetEvent.external_link || targetEvent.url || targetEvent.link || targetEvent.lien_primaire;
      if (previewUrl) {
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(`/evenements/${targetEvent.id}`);
      return;
    }

    setExpanded(true);
    setLoading(true);
    try {
      const res = await getPostersByPrayerCenter(pc.id);
      const list = res.data?.data || [];
      setPosters(list);
      setActiveIdx(0);

      if (list.length === 0) return;
      const firstEvent = list[0];
      const firstPreviewUrl = firstEvent.external_link || firstEvent.url || firstEvent.link || firstEvent.lien_primaire;
      if (firstPreviewUrl) {
        window.open(firstPreviewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(`/evenements/${firstEvent.id}`);
    } catch {
      setPosters([]);
    } finally {
      setLoading(false);
    }
  };

  const activeEvent = posters?.[activeIdx] || null;

  return (
    <div className="rep-map-block">
      {/* Clickable title */}
      <h6
        className="text-decoration-underline"
        style={{ cursor: 'pointer', color: '#8b1512', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        onClick={handleTitleClick}
        title="Voir les événements"
      >
        {pcLabel(pc.type_lieu, pc.nom)}:
        <span style={{ fontSize: 11, fontWeight: 400, color: '#aaa' }}>
          {expanded ? '▲' : '▼'}
        </span>
      </h6>

      {/* Map + caption always visible */}
      <div style={{ position: 'relative' }}>
        {pc.map_link ? (
          <iframe
            title={`map-${pc.id}`}
            src={toEmbedUrl(pc.map_link, pc.nom)}
            className="map-embed"
            allowFullScreen
            loading="lazy"
          />
        ) : null}
        <div className="map-caption shadow-sm">
          <div style={{ color: '#333' }}>{pc.lieu || pc.nom}</div>
          {pc.telephone_1 && (
            <div style={{ color: '#666', marginTop: 2 }}>
              {[pc.telephone_1, pc.telephone_2].filter(Boolean).join(' / ')}
            </div>
          )}
        </div>
      </div>

      {/* Events panel — shown when expanded */}
      {expanded && (
        <div style={{ marginTop: 12, background: '#faf9f7', borderRadius: 8, border: '1px solid #eee', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#aaa', fontSize: 13 }}>
              <FiCalendar size={20} style={{ display: 'block', margin: '0 auto 8px' }} />
              Chargement des événements…
            </div>
          ) : !posters || posters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
              <FiCalendar size={24} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.4 }} />
              <div style={{ fontSize: 13, fontStyle: 'italic' }}>
                Restez connectés, des événements à venir seront bientôt annoncés.
              </div>
            </div>
          ) : (
            <>
              {/* Tabs — only when multiple events */}
              {posters.length > 1 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                  {posters.map((ev, i) => (
                    <button
                      key={ev.id}
                      onClick={() => setActiveIdx(i)}
                      style={{
                        padding: '4px 12px',
                        borderRadius: 20,
                        border: '1px solid',
                        fontSize: 12,
                        fontWeight: activeIdx === i ? 700 : 400,
                        cursor: 'pointer',
                        borderColor: activeIdx === i ? '#8b1512' : '#ccc',
                        backgroundColor: activeIdx === i ? '#8b1512' : '#fff',
                        color: activeIdx === i ? '#fff' : '#555',
                        transition: 'all 0.15s',
                      }}
                    >
                      Événement {i + 1}
                    </button>
                  ))}
                </div>
              )}

              {/* Active event card */}
              {activeEvent && (
                <div
                  style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}
                  onClick={() => navigate(`/evenements/${activeEvent.id}`)}
                >
                  {activeEvent.image_url && (
                    <img
                      src={activeEvent.image_url}
                      alt={activeEvent.titre_evenement}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid #eee' }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: '#8b1512', fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <IoCalendarClearOutline size={12} />
                      {formatDate(activeEvent.date_evenement)}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#222', marginBottom: 4, lineHeight: 1.3 }}>
                      {activeEvent.titre_evenement}
                    </div>
                    {activeEvent.theme && (
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>{activeEvent.theme}</div>
                    )}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#8b1512', fontWeight: 600 }}>
                      En savoir plus <FaArrowRight size={11} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main page ── */
export default function RepresentationsPage() {
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  const { data, loading, error } = useFetch(
    getRepresentations,
    { limit: 100,statut:'active', ...(debouncedSearch && { pays: debouncedSearch }) },
    [debouncedSearch]
  );
  const representations = data?.data || [];

  useEffect(() => {
    if (representations.length > 0 && !selectedId) {
      setSelectedId(representations[0].id);
    }
  }, [representations.length]); // eslint-disable-line

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);
    getRepresentationById(selectedId)
      .then(res => {
        if (!cancelled) {
          setDetail(res.data?.data || null);
          setDetailLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const countryButtons = loading
    ? Array.from({ length: 6 }).map((_, i) => <SkeletonCountryBtn key={i} />)
    : representations.map(r => (
      <button
        key={r.id}
        className={`country-btn${selectedId === r.id ? ' selected' : ''}${r.is_headquarter ? ' headquarters' : ''}`}
        onClick={() => setSelectedId(r.id)}
        aria-label={`${r.pays}${r.is_headquarter ? ', siège' : ''}`}
      >
        {r.drapeau_url
          ? <img src={r.drapeau_url} alt={r.pays} style={{ width: 40, height: 30, borderRadius: 2 }} />
          : <FiMapPin size={20} />
        }
        <span>{r.pays}</span>
        {r.is_headquarter && <span className="headquarters-badge">SIÈGE</span>}
      </button>
    ));

  return (
    <div className="two-col">
      {/* Mobile layout */}
      <div className="rep-mobile-top">
        <div className="search-bar">
          <FiSearch size={18} />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-row hide-scrollbar">{countryButtons}</div>
      </div>

      {/* Desktop sidebar */}
      <aside className="sidebar-col">
        <div className="search-bar">
          <FiSearch size={18} />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-grid">{countryButtons}</div>
      </aside>

      <div className="main-col pt-3">
        {detailLoading ? (
          <SkeletonRepDetail />
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiMapPin size={32} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Impossible de charger les représentations.</p>
          </div>
        ) : !detail ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiMapPin size={32} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Aucune représentation trouvée.</p>
          </div>
        ) : (
          <div className="rep-detail">
            <h4 className="rep-country-title bg-white p-3 shadow-sm rounded justify-content-start">
              {detail.drapeau_url && (
                <img src={detail.drapeau_url} alt={detail.pays} style={{ width: 45, height: 30, marginRight: 8, borderRadius: 2 }} />
              )}
              {detail?.prefix + " "}{detail.pays}
            </h4>

            <div className="rep-field">
              <span className="rep-field-label">NOM REPRESENTANT:</span>
              <span>{detail.nom_representant}</span>
            </div>

            {(detail.secretaire_1_nom || detail.secretaire_2_nom) && (
              <div className="rep-field">
                <span className="rep-field-label">NOM DES SECRETAIRES:</span>
                <span>{[detail.secretaire_1_nom, detail.secretaire_2_nom].filter(Boolean).join(' / ')}</span>
              </div>
            )}

            {detail.numeros_representation?.length > 0 && (
              <div className="rep-field">
                <span className="rep-field-label">NUMEROS REPRESENTATION:</span>
                <div className="phone-grid">
                  {detail.numeros_representation.reduce((acc, num, i) => {
                    if (i % 2 === 0) acc.push([num]);
                    else acc[acc.length - 1].push(num);
                    return acc;
                  }, []).map((pair, i) => (
                    <div key={i} className="phone-row">
                      <span className="phone-col">{pair[0]}</span>
                      {pair[1] && <span className="phone-separator">|</span>}
                      {pair[1] && <span className="phone-col">{pair[1]}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* <div className="rep-field">
              <span className="rep-field-label">TÉLÉPHONE REPRÉSENTANT:</span>
              <div className="phone-grid">
                {[detail.telephone_representant_1, detail.telephone_representant_2].filter(Boolean).reduce((acc, num, i) => {
                  if (i % 2 === 0) acc.push([num]);
                  else acc[acc.length - 1].push(num);
                  return acc;
                }, []).map((pair, i) => (
                  <div key={i} className="phone-row">
                    <span className="phone-col">{pair[0]}</span>
                    {pair[1] && <span className="phone-separator">|</span>}
                    {pair[1] && <span className="phone-col">{pair[1]}</span>}
                  </div>
                ))}
              </div>
            </div> */}

            {(detail.secretaire_1_tel_1 || detail.secretaire_2_tel_1) && (
              <div className="rep-field">
                <span className="rep-field-label">NUMÉROS DU SECRETARIAT:</span>
                <div className="phone-grid">
                  {[detail.secretaire_1_tel_1, detail.secretaire_1_tel_2, detail.secretaire_2_tel_1, detail.secretaire_2_tel_2].filter(Boolean).reduce((acc, num, i) => {
                    if (i % 2 === 0) acc.push([num]);
                    else acc[acc.length - 1].push(num);
                    return acc;
                  }, []).map((pair, i) => (
                    <div key={i} className="phone-row">
                      <span className="phone-col">{pair[0]}</span>
                      {pair[1] && <span className="phone-separator">|</span>}
                      {pair[1] && <span className="phone-col">{pair[1]}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prayer centers — each with clickable title → inline event panel */}
            <div className="rep-maps">
              {(detail.prayer_centers || []).map((pc) => (
                <PrayerCenterBlock key={pc.id} pc={pc} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
